import React, { useState } from "react";

import { ColumnType } from "antd/es/table";

import { getProgramInternalName, useCreateCarbonProgramDraft, useGetAllCarbonPrograms, useGetCarbonProgramDrafts, useNavigate } from "utils";

import { CARBON_PROGRAM_DRAFT_KIND, CARBON_PROGRAM_DRAFT_STATUS, CarbonProgramDraftKind, CarbonProgramDraftStatus, CarbonProgramDraftSummary } from "models";

import { Button, Col, Flex, Link, ModalContent, Option, Row, Select, Table as TableJs, Typography } from "components";

import { BLANK_PROGRAM_CONFIG, copyOfProgram } from "pages/admin/carbonPrograms/drafts/draftConfig";
import { DraftStatusTag, adminUserName, draftKindLabel, draftProgramName, formatDraftDate } from "pages/admin/carbonPrograms/drafts/draftDisplay";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Table = TableJs as React.FC<any>;

const NewDraftModal = ({ visible, onClose }: { visible: boolean; onClose: () => void }) => {
  const navigate = useNavigate();
  const { data: livePrograms = [] } = useGetAllCarbonPrograms();
  const createDraftMutation = useCreateCarbonProgramDraft();
  const [kind, setKind] = useState<CarbonProgramDraftKind>(CARBON_PROGRAM_DRAFT_KIND.create);
  const [programId, setProgramId] = useState<string | undefined>(undefined);

  const isChange = kind === CARBON_PROGRAM_DRAFT_KIND.update;

  const handleCreate = () => {
    const sourceProgram = livePrograms.find((program) => program.id === programId);
    const request = isChange ? { kind, programId } : { kind, config: sourceProgram ? copyOfProgram(sourceProgram) : BLANK_PROGRAM_CONFIG };

    createDraftMutation.mutate(request, { onSuccess: (draft) => navigate(`/carbon-programs/drafts/${draft.id}`) });
  };

  return (
    <ModalContent visible={visible} onCancel={onClose}>
      <Flex vertical gap={16}>
        <Typography variant="h3">Start a program draft</Typography>
        <Select label="What do you need?" value={kind} onChange={(value) => setKind(value)}>
          <Option value={CARBON_PROGRAM_DRAFT_KIND.create}>A new carbon program</Option>
          <Option value={CARBON_PROGRAM_DRAFT_KIND.update}>A change to a live program</Option>
        </Select>
        <Select label={isChange ? "Program to change" : "Start from a copy of (optional)"} value={programId} onChange={setProgramId} showSearch allowClear>
          {livePrograms.map((program) => (
            <Option key={program.id} value={program.id}>
              {getProgramInternalName(program)}
            </Option>
          ))}
        </Select>
        <Typography variant="paragraph-s" color="grey-600">
          {isChange
            ? "The draft starts with the program's current settings. Nothing changes for farmers until the draft is approved and rolled out."
            : "Copying a similar program saves time. You can change every setting before submitting."}
        </Typography>
        <Button type="primary" onClick={handleCreate} disabled={isChange && !programId} loading={createDraftMutation.isPending}>
          Create draft
        </Button>
      </Flex>
    </ModalContent>
  );
};

export const Drafts = () => {
  const navigate = useNavigate();
  const draftsQuery = useGetCarbonProgramDrafts();
  const [isNewDraftModalOpen, setIsNewDraftModalOpen] = useState(false);

  const columns: ColumnType<CarbonProgramDraftSummary>[] = [
    {
      title: "program",
      key: "program",
      className: "paragraph-xs-bold",
      render: (_value, draft) => <Link onClick={() => navigate(`/carbon-programs/drafts/${draft.id}`)}>{draftProgramName(draft)}</Link>,
    },
    {
      title: "type",
      key: "kind",
      className: "paragraph-xs-bold",
      render: (_value, draft) => <Typography variant="paragraph-m">{draftKindLabel(draft)}</Typography>,
    },
    {
      title: "status",
      dataIndex: "status",
      key: "status",
      className: "paragraph-xs-bold",
      filters: Object.values(CARBON_PROGRAM_DRAFT_STATUS).map((status) => ({ text: status, value: status })),
      onFilter: (value, draft) => draft.status === value,
      render: (status: CarbonProgramDraftStatus) => <DraftStatusTag status={status} />,
    },
    {
      title: "requested by",
      key: "createdBy",
      className: "paragraph-xs-bold",
      render: (_value, draft) => <Typography variant="paragraph-m">{adminUserName(draft.createdBy)}</Typography>,
    },
    {
      title: "last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      className: "paragraph-xs-bold",
      sorter: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
      render: (updatedAt: string) => <Typography variant="paragraph-m">{formatDraftDate(updatedAt)}</Typography>,
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" className="margin-bottom-150">
        <Typography variant="paragraph-m" color="grey-600">
          Request a new carbon program or a change to a live one. Product &amp; engineering review each draft before it goes live.
        </Typography>
        <Button type="primary" onClick={() => setIsNewDraftModalOpen(true)}>
          New draft
        </Button>
      </Flex>
      <Row>
        <Col>
          <Table data={(draftsQuery.data ?? []).map((draft) => ({ ...draft, key: draft.id }))} columns={columns} loading={draftsQuery.isLoading} />
        </Col>
      </Row>
      {isNewDraftModalOpen && <NewDraftModal visible={isNewDraftModalOpen} onClose={() => setIsNewDraftModalOpen(false)} />}
    </>
  );
};
